import { useEffect, useState } from "react";
import { LoaderBoxCircular } from "../Components/LoaderBox";
import {
  Box, Container, TextField, Grid, Divider, Button,
  FormControlLabel, Switch, Typography
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { DialogAlert } from "../Components/DialogAlert";
import { instance } from "../Components/config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Components/AuthContext";

function formatoFecha(fecha) {
  if (!fecha) return "";
  return new Date(fecha).toISOString().slice(0, 10);
}

function UPSE8Configuracion() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [anio, setAnio] = useState(new Date().getFullYear());
  const [v1Inicio, setV1Inicio] = useState("");
  const [v1Fin, setV1Fin] = useState("");
  const [v2Inicio, setV2Inicio] = useState("");
  const [v2Fin, setV2Fin] = useState("");
  const [v3Inicio, setV3Inicio] = useState("");
  const [v3Fin, setV3Fin] = useState("");
  const [fechaTope, setFechaTope] = useState("");
  const [bloquearDespuesTope, setBloquearDespuesTope] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [configuracionExistente, setConfiguracionExistente] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialog, setDialog] = useState({ title: "", text: "" });

  const limpiar = () => {
    setV1Inicio(""); setV1Fin("");
    setV2Inicio(""); setV2Fin("");
    setV3Inicio(""); setV3Fin("");
    setFechaTope("");
    setBloquearDespuesTope(false);
    setConfiguracionExistente(false);
  };

  useEffect(() => {
    if (!anio) {
      limpiar();
      return;
    }
    setIsLoading(true);
    instance.get(`/upse8Configuracion/getByAnio/${anio}`)
      .then((res) => {
        if (res.data) {
          setV1Inicio(formatoFecha(res.data.V1_Inicio));
          setV1Fin(formatoFecha(res.data.V1_Fin));
          setV2Inicio(formatoFecha(res.data.V2_Inicio));
          setV2Fin(formatoFecha(res.data.V2_Fin));
          setV3Inicio(formatoFecha(res.data.V3_Inicio));
          setV3Fin(formatoFecha(res.data.V3_Fin));
          setFechaTope(formatoFecha(res.data.Fecha_Tope));
          setBloquearDespuesTope(!!res.data.Bloquear_Despues_Tope);
          setConfiguracionExistente(true);
        } else {
          limpiar();
        }
      })
      .catch(() => limpiar())
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anio]);

  const guardar = (e) => {
    e.preventDefault();

    if (!v1Inicio || !v1Fin || !v2Inicio || !v2Fin || !v3Inicio || !v3Fin || !fechaTope) {
      setDialog({ title: 'Falta información', text: 'Debe completar las 3 ventanas de presentación y la fecha tope' });
      setOpenDialog(true);
      return;
    }

    setIsLoading(true);
    instance.post('/upse8Configuracion/guardar', {
      anio,
      v1Inicio, v1Fin,
      v2Inicio, v2Fin,
      v3Inicio, v3Fin,
      fechaTope,
      bloquearDespuesTope,
      idUsuario: user.id
    })
      .then(() => {
        setConfiguracionExistente(true);
        setDialog({ title: 'Aviso', text: 'Se guardó correctamente la configuración de plazos' });
      })
      .catch((err) => {
        if (err.response?.status === 500) {
          setDialog({
            title: "ERROR",
            text: "Existe algún problema con el servidor, por favor inténtalo de nuevo mas tarde, si el problema persiste contáctate con alguno de los encargados.",
          });
        } else {
          setDialog({ title: "ERROR", text: err.response?.data?.message });
        }
      })
      .finally(() => {
        setIsLoading(false);
        setOpenDialog(true);
      });
  };

  return (
    <Container>
      <Box sx={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Volver</Button>
        <h2 style={{ textAlign: 'center', flex: 1, margin: 0 }}>Configuración de plazos UPSE-8</h2>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <LoaderBoxCircular isLoading={isLoading}>
          <Container maxWidth="md" sx={{ marginBottom: 5 }}>
            <Box
              sx={{
                padding: "4px",
                margin: { xs: 0, sm: 1 },
                border: { xs: "0", sm: "1px solid #000" },
              }}
              component="form"
              onSubmit={guardar}
            >
              <Grid container spacing={3} padding={2} rowSpacing={1} sx={{ alignItems: "center" }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Define las 3 ventanas oficiales de presentación (una por cuatrimestre) y la fecha tope
                    para presentaciones fuera de ventana, según el año fiscal.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    required
                    fullWidth
                    label="Año"
                    type="number"
                    value={anio}
                    onChange={(e) => setAnio(Number(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  {configuracionExistente
                    ? <Typography variant="body2" color="success.main">Ya existe una configuración guardada para este año.</Typography>
                    : <Typography variant="body2" color="text.secondary">No hay configuración guardada para este año todavía.</Typography>}
                </Grid>

                <Grid item xs={12}><Divider variant="middle" component="aside" /></Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Primera ventana (primer cuatrimestre)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Inicio" value={v1Inicio} onChange={(e) => setV1Inicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Fin" value={v1Fin} onChange={(e) => setV1Fin(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Segunda ventana (segundo cuatrimestre)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Inicio" value={v2Inicio} onChange={(e) => setV2Inicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Fin" value={v2Fin} onChange={(e) => setV2Fin(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">Tercera ventana (tercer cuatrimestre)</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Inicio" value={v3Inicio} onChange={(e) => setV3Inicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Fin" value={v3Fin} onChange={(e) => setV3Fin(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>

                <Grid item xs={12}><Divider variant="middle" component="aside" /></Grid>

                <Grid item xs={12} sm={6}>
                  <TextField required fullWidth type="date" label="Fecha tope (caso fortuito)" value={fechaTope} onChange={(e) => setFechaTope(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={<Switch checked={bloquearDespuesTope} onChange={(e) => setBloquearDespuesTope(e.target.checked)} />}
                    label="Bloquear presentaciones después de la fecha tope"
                  />
                </Grid>

                <Grid item sm={4} />
                <Grid item xs={12} sm={4}>
                  <Button fullWidth variant="contained" type="submit" startIcon={<Save />}>Guardar</Button>
                </Grid>
                <Grid item sm={4} />
              </Grid>
            </Box>
          </Container>
        </LoaderBoxCircular>
      </Box>
      <DialogAlert
        title={dialog.title}
        text={dialog.text}
        open={openDialog}
        setOpen={setOpenDialog}
      />
    </Container>
  );
}

export { UPSE8Configuracion };
